/**
 * Mutations that write invoice-preview lines back onto the work order itself
 * (#1959). The generated invoice later copies these 1:1 — there is no
 * separate billing draft while the work order is still open.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveServiceDetails, type ServiceDetailsBase } from '@/components/workorders/billing/serviceDetailsConcurrency';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import {
  LateResultError,
  PRICING_READ_PERMISSION,
  PricingAccessError,
  fenceHolds,
  readPricingAccess,
  takeFence,
  type PricingAccess,
} from '@/features/inventory/pricing/pricingSession';
import { queryKeys } from './queryKeys';
import type { WorkOrderItem } from './useWorkOrders';
import type {
  ServiceDetailsPayload,
  WorkOrderConsumedPart,
} from '@/components/workorders/billing/billingLineSource';

export interface UpdateServiceDetailsVars {
  workOrderId: string;
  bookingServiceId: string;
  data: ServiceDetailsPayload;
  base: ServiceDetailsBase;
}

export interface ManualLineCreateData {
  description: string;
  category?: string;
  partsCostCents: number;
  laborCostCents: number;
  estimatedHours?: number;
}

export interface ManualLineUpdateData {
  description?: string;
  category?: string;
  partsCostCents?: number;
  laborCostCents?: number;
  estimatedHours?: number;
}

/**
 * Body of `PATCH /work-orders/:id/parts/:partId` (`updatePartSchema`).
 *
 * The price follows the server's rules (#3034): a quantity or discount edit keeps the stored unit price and its source
 * (send NO price for it); `unitSellPriceCents` is a person's price and makes the line manual; `reprice: true` explicitly asks
 * the server to look the price of a catalog line up again against the current rules. The server refuses the last two together,
 * so the type does too.
 */
export type ConsumedPartUpdateData =
  | { quantity?: number; unitSellPriceCents?: number; discountPercent?: number; reprice?: undefined }
  | { quantity?: number; discountPercent?: number; reprice: boolean; unitSellPriceCents?: undefined };

const WORK_ORDER_UPDATE_PERMISSION = 'work_orders.update';

/** What an explicit reprice needs on top of the route's own `work_orders.update`: reading current part prices. */
const REPRICE_PERMISSIONS: readonly string[] = [WORK_ORDER_UPDATE_PERMISSION, PRICING_READ_PERMISSION];

/** The owner a reprice was offered under: tenant, user, session family and in-memory generation. */
export type RepriceOwner = Pick<PricingAccess, 'owner' | 'epoch'>;

/** Variables of `updateConsumedPart`. `repriceOwner` is only read for `data.reprice`; see `bindRepriceOwner`. */
export interface UpdateConsumedPartVars {
  workOrderId: string;
  partId: string;
  data: ConsumedPartUpdateData;
  repriceOwner?: RepriceOwner | undefined;
}

/** Refuses before anything is sent when the live session may not reprice (signed out, locked, or a permission missing now). */
function assertMayReprice(): void {
  const state = useAuthStore.getState();
  if (!readPricingAccess(state).active) throw new PricingAccessError(['session']);
  const missing = REPRICE_PERMISSIONS.filter((permission) => !state.hasPermission(permission));
  if (missing.length > 0) throw new PricingAccessError(missing);
}

/**
 * Binds an explicit reprice to the owner that offered it. Permissions alone do not say WHOSE intent it is, and a mutation can
 * be dispatched later than it was offered, so the owner (tenant, user, session family, generation) is captured when the
 * mutation is offered (the live session at that call, unless the caller names the owner of its own intent) and compared with
 * the live session again at dispatch. Edits without `reprice` are untouched.
 */
function bindRepriceOwner(vars: UpdateConsumedPartVars): UpdateConsumedPartVars {
  if (vars.data.reprice !== true || vars.repriceOwner !== undefined) return vars;
  const { owner, epoch } = readPricingAccess();
  return { ...vars, repriceOwner: { owner, epoch } };
}

const EMPTY_CONSUMED_PARTS: WorkOrderConsumedPart[] = [];

function invalidateWorkOrderBilling(queryClient: ReturnType<typeof useQueryClient>, id: string): void {
  // Same set as useWorkOrders.invalidateWorkOrder so the Työt card and the
  // invoice preview refresh together when hours/prices change.
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.history(id) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.stats() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(id) });
  void queryClient.invalidateQueries({ queryKey: ['invoicePreview', id] });
  void queryClient.invalidateQueries({ queryKey: ['workOrderParts', id] });
}

export function useWorkOrderBillingLines(workOrderId?: string) {
  const queryClient = useQueryClient();

  const consumedPartsQuery = useQuery({
    queryKey: ['workOrderParts', workOrderId ?? ''],
    queryFn: () => {
      if (workOrderId == null || workOrderId.length === 0) return Promise.resolve([]);
      return api.get<WorkOrderConsumedPart[]>(`/work-orders/${workOrderId}/parts`);
    },
    enabled: workOrderId != null && workOrderId.length > 0,
  });

  const updateServiceDetails = useMutation({
    mutationFn: (input: UpdateServiceDetailsVars) => saveServiceDetails(input),
    onError: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const addManualLine = useMutation({
    mutationFn: ({
      workOrderId,
      data,
    }: {
      workOrderId: string;
      data: ManualLineCreateData;
    }) => api.post<WorkOrderItem>(`/work-orders/${workOrderId}/items`, data),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const updateManualLine = useMutation({
    mutationFn: ({
      workOrderId,
      itemId,
      data,
    }: {
      workOrderId: string;
      itemId: string;
      data: ManualLineUpdateData;
    }) => api.patch<WorkOrderItem>(`/work-orders/${workOrderId}/items/${itemId}`, data),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const deleteManualLine = useMutation({
    mutationFn: ({
      workOrderId,
      itemId,
    }: {
      workOrderId: string;
      itemId: string;
    }) => api.delete(`/work-orders/${workOrderId}/items/${itemId}`),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const updateConsumedPartMutation = useMutation({
    mutationFn: async ({ workOrderId, partId, data, repriceOwner }: UpdateConsumedPartVars) => {
      // Edits without `reprice` go out exactly as they always did.
      if (data.reprice !== true) return api.patch(`/work-orders/${workOrderId}/parts/${partId}`, data);
      // Explicit reprice only (#3034): refused here, before a request, when the live session may not do it, or is not the
      // owner (tenant, user, session family, generation) that offered it.
      assertMayReprice();
      const fence = takeFence(repriceOwner ?? { owner: null, epoch: 0 }, REPRICE_PERMISSIONS);
      let value: unknown;
      try {
        value = await api.patch(`/work-orders/${workOrderId}/parts/${partId}`, data);
      } catch (error) {
        // The request WAS sent; an answer for a session that is gone is carried, not reported (`isSessionCancel`).
        if (!fenceHolds(fence)) throw new LateResultError({ ok: false, error });
        throw error;
      }
      if (!fenceHolds(fence)) throw new LateResultError({ ok: true, value });
      return value;
    },
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
    // The server decided even when nobody on this screen may hear it: the cached work order must not stay stale.
    onError: (error, { workOrderId }) => {
      if (error instanceof LateResultError) invalidateWorkOrderBilling(queryClient, workOrderId);
    },
  });
  // Both entry points bind a reprice to the owner at the moment it is offered, before react-query may queue or delay it.
  const updateConsumedPart = {
    ...updateConsumedPartMutation,
    mutate: (vars: UpdateConsumedPartVars, options?: Parameters<typeof updateConsumedPartMutation.mutate>[1]) =>
      updateConsumedPartMutation.mutate(bindRepriceOwner(vars), options),
    mutateAsync: (vars: UpdateConsumedPartVars, options?: Parameters<typeof updateConsumedPartMutation.mutateAsync>[1]) =>
      updateConsumedPartMutation.mutateAsync(bindRepriceOwner(vars), options),
  };

  const deleteConsumedPart = useMutation({
    mutationFn: ({
      workOrderId,
      partId,
    }: {
      workOrderId: string;
      partId: string;
    }) => api.delete(`/work-orders/${workOrderId}/parts/${partId}`),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const isPending =
    updateServiceDetails.isPending
    || addManualLine.isPending
    || updateManualLine.isPending
    || deleteManualLine.isPending
    || updateConsumedPart.isPending
    || deleteConsumedPart.isPending;

  return {
    updateServiceDetails,
    addManualLine,
    updateManualLine,
    deleteManualLine,
    updateConsumedPart,
    deleteConsumedPart,
    consumedParts: consumedPartsQuery.data ?? EMPTY_CONSUMED_PARTS,
    isPending,
  };
}
